import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Phone, CheckSquare } from 'lucide-react'
import { getUsers } from '../../services/staffServices'
import StaffLayout from './components/StaffLayout'
import { StaggerContainer, StaggerItem } from './components/StaffAnimations'

const ROLE_COLORS = {
    ADMIN: 'bg-purple-500/15 text-purple-400 border border-purple-500/20',
    MANAGER: 'bg-blue-500/15 text-blue-400 border border-blue-500/20',
    STAFF: 'bg-slate-500/15 text-slate-400 border border-slate-500/20',
}

// Avatar color based on name
const AVATAR_COLORS = [
    'bg-blue-600', 'bg-purple-600', 'bg-green-600',
    'bg-amber-600', 'bg-rose-600', 'bg-indigo-600', 'bg-teal-600',
]

function StaffCard({ user }) {
    const colorIdx = user.name ? user.name.charCodeAt(0) % AVATAR_COLORS.length : 0
    const isAvailable = user.status === 'AVAILABLE'
    const joinDate = user.created_at
        ? new Date(user.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
        : null

    return (
        <div className="bg-slate-900 rounded-xl border border-white/5 p-5 hover:border-white/10 transition-all group">
            <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="relative shrink-0">
                    {user.profile_photo_url ? (
                        <img
                            src={user.profile_photo_url}
                            alt={user.name}
                            className="w-12 h-12 rounded-full object-cover"
                        />
                    ) : (
                        <div className={`w-12 h-12 rounded-full ${AVATAR_COLORS[colorIdx]} flex items-center justify-center text-base font-bold text-white`}>
                            {user.name?.[0]?.toUpperCase() || '?'}
                        </div>
                    )}
                    {/* Status dot */}
                    <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-slate-900 ${isAvailable ? 'bg-green-400' : 'bg-slate-500'}`} />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <p className="font-semibold text-white truncate">{user.name}</p>
                            {user.username && <p className="text-xs text-slate-500">@{user.username}</p>}
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${ROLE_COLORS[user.role] || ROLE_COLORS.STAFF}`}>
                            {user.role}
                        </span>
                    </div>

                    {/* Status badge */}
                    <div className="flex items-center gap-1.5 mt-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${isAvailable ? 'bg-green-400' : 'bg-slate-500'}`} />
                        <span className={`text-xs ${isAvailable ? 'text-green-400' : 'text-slate-500'}`}>
                            {isAvailable ? 'Available' : user.status || 'Away'}
                        </span>
                    </div>

                    {/* Department */}
                    {user.department && (
                        <p className="text-xs text-slate-400 mt-1 truncate">{user.department}</p>
                    )}

                    {/* Phone */}
                    {user.phone_number && (
                        <a
                            href={`tel:${user.phone_number}`}
                            className="flex items-center gap-1.5 mt-2 text-xs text-slate-400 hover:text-blue-400 transition w-fit"
                        >
                            <Phone size={11} />
                            <span>{user.phone_number}</span>
                        </a>
                    )}

                    {/* Active tasks + join date */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                        {user.active_tasks_count > 0 ? (
                            <span className="flex items-center gap-1 text-xs text-amber-400">
                                <CheckSquare size={11} />
                                {user.active_tasks_count} active task{user.active_tasks_count !== 1 ? 's' : ''}
                            </span>
                        ) : (
                            <span className="text-xs text-slate-600">No active tasks</span>
                        )}
                        {joinDate && (
                            <span className="text-[10px] text-slate-600">Joined {joinDate}</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default function Team() {
    const [search, setSearch] = useState('')
    const [roleFilter, setRoleFilter] = useState('ALL')
    const { data: users = [], isLoading } = useQuery({ queryKey: ['users'], queryFn: getUsers })

    const roles = ['ALL', 'ADMIN', 'MANAGER', 'STAFF']

    const filtered = users.filter(u => {
        const matchSearch =
            !search ||
            u.name?.toLowerCase().includes(search.toLowerCase()) ||
            u.email?.toLowerCase().includes(search.toLowerCase()) ||
            u.department?.toLowerCase().includes(search.toLowerCase()) ||
            u.phone_number?.includes(search)
        const matchRole = roleFilter === 'ALL' || u.role === roleFilter
        return matchSearch && matchRole
    })

    const totalActive = users.filter(u => u.is_active).length
    const totalAvailable = users.filter(u => u.status === 'AVAILABLE').length

    return (
        <StaffLayout>
            <div className="space-y-5">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-white">
                        Team
                        <span className="text-slate-500 font-normal text-base ml-2">({users.length})</span>
                    </h1>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-4">
                        <p className="text-xs text-slate-400">Total Staff</p>
                        <p className="text-2xl font-bold text-white mt-1">{users.length}</p>
                    </div>
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-4">
                        <p className="text-xs text-slate-400">Active</p>
                        <p className="text-2xl font-bold text-green-400 mt-1">{totalActive}</p>
                    </div>
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-4">
                        <p className="text-xs text-slate-400">Available Now</p>
                        <p className="text-2xl font-bold text-blue-400 mt-1">{totalAvailable}</p>
                    </div>
                </div>

                {/* Search + role filter chips */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search by name, department, phone..."
                            className="w-full bg-slate-900 border border-white/5 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <div className="flex gap-2">
                        {roles.map(role => (
                            <button
                                key={role}
                                onClick={() => setRoleFilter(role)}
                                className={`px-3 py-2 rounded-xl text-xs font-medium transition ${roleFilter === role
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-slate-900 border border-white/5 text-slate-400 hover:text-white'
                                    }`}
                            >
                                {role}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Cards */}
                {isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Array(6).fill(0).map((_, i) => (
                            <div key={i} className="bg-slate-900 rounded-xl border border-white/5 p-5 animate-pulse h-36" />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-12 text-center text-slate-500">
                        No staff members found
                    </div>
                ) : (
                    <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filtered.map(u => <StaggerItem key={u.id}><StaffCard user={u} /></StaggerItem>)}
                    </StaggerContainer>
                )}
            </div>
        </StaffLayout>
    )
}
